import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  GraduationCap, BookOpen, ChevronRight, ChevronLeft, Check, Loader2,
  User, Phone, Mail, MapPin, Shield, Calendar, Lock, Eye, EyeOff,
  Briefcase, IndianRupee, Gift, Tag, CreditCard, Upload, FileText, X
} from 'lucide-react';
import { useRazorpay } from 'react-razorpay';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STEPS = [
  { id: 'course', label: 'Courses', icon: BookOpen },
  { id: 'personal', label: 'Personal', icon: User },
  { id: 'education', label: 'Education', icon: GraduationCap },
  { id: 'guardian', label: 'Guardian', icon: Shield },
  { id: 'documents', label: 'Documents', icon: Upload },
  { id: 'account', label: 'Account', icon: Lock },
  { id: 'review', label: 'Review', icon: Check },
  { id: 'payment', label: 'Payment', icon: CreditCard },
];

const fmtPrice = (n) => `\u20B9${(n || 0).toLocaleString('en-IN')}`;

export default function Apply() {
  const { theme } = useTheme();
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const { Razorpay, isLoading: razorpayLoading } = useRazorpay();

  const [programType, setProgramType] = useState('vocational');
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [offers, setOffers] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [paymentDone, setPaymentDone] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [paymentMode, setPaymentMode] = useState('lumpsum'); // 'lumpsum' or 'emi'
  const EMI_MIN = 5000;
  const [applicationId, setApplicationId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});

  // Validation helpers
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => /^[\+]?[0-9\s\-]{10,15}$/.test(phone.replace(/\s/g, ''));
  const validateAadhaar = (aadhaar) => !aadhaar || /^[0-9\s]{12,14}$/.test(aadhaar.replace(/\s/g, ''));
  const validateYear = (year) => !year || /^[0-9]{4}$/.test(year);
  const validateName = (name) => name && name.trim().length >= 2 && /^[a-zA-Z\s.']+$/.test(name);
  const validatePercentage = (val) => !val || /^[0-9.]+%?$|^[A-Za-z+]+$/.test(val);

  const validateStep = (step) => {
    const newErrors = {};
    switch (step) {
      case 0:
        if (selectedIds.length === 0) newErrors._step0 = 'Select at least one course';
        if (!form.skill_level) newErrors.skill_level = 'Required';
        if (!form.batch_preferred) newErrors.batch_preferred = 'Required';
        if (!form.mode) newErrors.mode = 'Required';
        break;
      case 1:
        if (!form.full_name || form.full_name.trim().length < 2) newErrors.full_name = 'Enter a valid full name (min 2 characters)';
        else if (!validateName(form.full_name)) newErrors.full_name = 'Name can only contain letters, spaces, dots and apostrophes';
        if (!form.gender) newErrors.gender = 'Select your gender';
        if (!form.date_of_birth) newErrors.date_of_birth = 'Select date of birth';
        if (!form.mobile) newErrors.mobile = 'Mobile number is required';
        else if (!validatePhone(form.mobile)) newErrors.mobile = 'Enter a valid phone number (10-15 digits)';
        if (!form.email) newErrors.email = 'Email is required';
        else if (!validateEmail(form.email)) newErrors.email = 'Enter a valid email (e.g., name@example.com)';
        if (form.aadhaar && !validateAadhaar(form.aadhaar)) newErrors.aadhaar = 'Aadhaar must be 12 digits';
        break;
      case 2:
        if (!form.highest_qualification) newErrors.highest_qualification = 'Select your qualification';
        if (form.year_of_passing && !validateYear(form.year_of_passing)) newErrors.year_of_passing = 'Enter a valid year (e.g., 2024)';
        if (form.percentage_grade && !validatePercentage(form.percentage_grade)) newErrors.percentage_grade = 'Enter valid percentage or grade (e.g., 85% or A+)';
        break;
      case 3:
        if (!form.guardian_name || form.guardian_name.trim().length < 2) newErrors.guardian_name = 'Enter guardian name';
        if (!form.guardian_contact) newErrors.guardian_contact = 'Contact number is required';
        else if (!validatePhone(form.guardian_contact)) newErrors.guardian_contact = 'Enter a valid phone number (10-15 digits)';
        break;
      case 5:
        if (!form.password) newErrors.password = 'Password is required';
        else if (form.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
        if (!form.confirm_password) newErrors.confirm_password = 'Confirm your password';
        else if (form.password !== form.confirm_password) newErrors.confirm_password = 'Passwords do not match';
        break;
      case 6:
        if (!agreed) newErrors.agreed = 'You must accept the declaration';
        break;
      default: break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Sanitized input handlers
  const handlePhoneInput = (key, value) => {
    const sanitized = value.replace(/[^0-9+\s\-]/g, '');
    update(key, sanitized);
    if (errors[key]) setErrors(e => ({ ...e, [key]: undefined }));
  };

  const handleAadhaarInput = (value) => {
    const sanitized = value.replace(/[^0-9\s]/g, '');
    update('aadhaar', sanitized);
    if (errors.aadhaar) setErrors(e => ({ ...e, aadhaar: undefined }));
  };

  const handleYearInput = (value) => {
    const sanitized = value.replace(/[^0-9]/g, '').slice(0, 4);
    update('year_of_passing', sanitized);
    if (errors.year_of_passing) setErrors(e => ({ ...e, year_of_passing: undefined }));
  };

  const clearError = (key) => {
    if (errors[key]) setErrors(e => ({ ...e, [key]: undefined }));
  };

  const FieldError = ({ field }) => errors[field] ? <p className="text-xs text-red-500 mt-1">{errors[field]}</p> : null;

  // selected_courses stores: { [course_id]: { tier: 'self_learning' | 'instructor_led' } }
  const [selectedCourses, setSelectedCourses] = useState({});

  const [form, setForm] = useState({
    skill_level: '', batch_preferred: '', mode: '',
    full_name: '', gender: '', date_of_birth: '', mobile: '', email: '', aadhaar: '',
    current_address: '', permanent_address: '',
    highest_qualification: '', school_college: '', percentage_grade: '', year_of_passing: '',
    guardian_name: '', guardian_contact: '', guardian_occupation: '',
    password: '', confirm_password: '',
    documents: { id_proof: null, photo: null, education_proof: null }
  });

  useEffect(() => {
    axios.get(`${API}/courses`).then(r => setCourses(r.data)).catch(() => {});
    axios.get(`${API}/admissions/offers`).then(r => setOffers(r.data)).catch(() => {});
  }, []);

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const toggleCourse = (courseId, tier) => {
    setSelectedCourses(prev => {
      const next = { ...prev };
      if (next[courseId] && next[courseId].tier === tier) {
        delete next[courseId];
      } else {
        next[courseId] = { tier };
      }
      return next;
    });
  };

  const vocationalCourses = courses.filter(c => c.category_id !== 'cat_grad');
  const bdesCourses = courses.filter(c => c.category_id === 'cat_grad');
  const filteredCourses = programType === 'vocational' ? vocationalCourses : bdesCourses;
  const selectedIds = Object.keys(selectedCourses);

  const getPrice = (course, tier) => {
    const pt = course.pricing_tiers || {};
    if (tier === 'self_learning' && pt.self_learning) return pt.self_learning.price || course.price || 0;
    if (tier === 'instructor_led' && pt.instructor_led) return pt.instructor_led.price || course.price || 0;
    return course.price || 0;
  };

  // Pricing calculation with offers
  const pricing = useMemo(() => {
    const items = Object.entries(selectedCourses).map(([cid, { tier }]) => {
      const c = courses.find(x => x.course_id === cid);
      return c ? { course_id: cid, title: c.title, tier, price: getPrice(c, tier) } : null;
    }).filter(Boolean);

    const subtotal = items.reduce((sum, i) => sum + i.price, 0);

    let discount = 0;
    let freeCourses = [];
    let appliedOffer = null;

    for (const offer of offers) {
      const req = offer.required_courses || [];
      if (req.length > 0 && req.every(id => selectedIds.includes(id))) {
        if ((offer.discount_amount || 0) > discount) {
          discount = offer.discount_amount;
          appliedOffer = offer;
        }
        if (offer.free_course_id && !freeCourses.includes(offer.free_course_id)) {
          freeCourses.push(offer.free_course_id);
        }
      }
    }

    const freeCourseObjs = courses.filter(c => freeCourses.includes(c.course_id));
    const total = Math.max(0, subtotal - discount);
    return { items, subtotal, discount, total, freeCourses, freeCourseObjs, appliedOffer };
  }, [selectedCourses, courses, offers, selectedIds]);

  const handleDocUpload = async (docType, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File must be under 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);
      const res = await axios.post(`${API}/admissions/upload-doc`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(f => ({
        ...f,
        documents: { ...f.documents, [docType]: { filename: res.data.filename, url: res.data.url, original_name: file.name } }
      }));
      toast.success(`${docType.replace('_', ' ')} uploaded`);
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return selectedIds.length > 0 && form.skill_level && form.batch_preferred && form.mode;
      case 1: return form.full_name && form.gender && form.date_of_birth && form.mobile && validatePhone(form.mobile) && form.email && validateEmail(form.email);
      case 2: return form.highest_qualification;
      case 3: return form.guardian_name && form.guardian_contact && validatePhone(form.guardian_contact);
      case 4: return true;
      case 5: return form.password && form.password.length >= 6 && form.password === form.confirm_password;
      case 6: return agreed;
      case 7: return paymentDone;
      default: return false;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep === 6) {
        handleCreateApplication();
      } else {
        setCurrentStep(s => Math.min(s + 1, STEPS.length - 1));
      }
    } else {
      toast.error('Please fix the highlighted errors before proceeding');
    }
  };

  const handleBack = () => {
    setErrors({});
    setCurrentStep(s => Math.max(s - 1, 0));
  };

  const handleCreateApplication = async () => {
    if (!agreed) { toast.error('Please accept the declaration'); return; }
    setLoading(true);
    try {
      const courseSelections = Object.entries(selectedCourses).map(([cid, { tier }]) => {
        const c = courses.find(x => x.course_id === cid);
        return { course_id: cid, tier, price: getPrice(c, tier) };
      });

      const res = await axios.post(`${API}/admissions/apply`, {
        program_type: programType,
        course_selections: courseSelections,
        free_courses: pricing.freeCourses,
        discount_amount: pricing.discount,
        offer_name: pricing.appliedOffer?.name || '',
        skill_level: form.skill_level,
        batch_preferred: form.batch_preferred,
        mode: form.mode,
        full_name: form.full_name,
        gender: form.gender,
        date_of_birth: form.date_of_birth,
        mobile: form.mobile,
        email: form.email,
        aadhaar: form.aadhaar,
        current_address: form.current_address,
        permanent_address: form.permanent_address,
        highest_qualification: form.highest_qualification,
        school_college: form.school_college,
        percentage_grade: form.percentage_grade,
        year_of_passing: form.year_of_passing,
        guardian_name: form.guardian_name,
        guardian_contact: form.guardian_contact,
        guardian_occupation: form.guardian_occupation,
        documents: form.documents,
        password: form.password
      });

      setApplicationId(res.data.application_id);
      localStorage.setItem('auth_token', res.data.access_token);
      setCurrentStep(7);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { Authorization: `Bearer ${token}` };
      const payAmount = paymentMode === 'emi' ? EMI_MIN : pricing.total;
      const orderRes = await axios.post(`${API}/admissions/payment/create`, {
        application_id: applicationId,
        amount: payAmount,
        payment_mode: paymentMode
      }, { headers, withCredentials: true });

      const options = {
        key: orderRes.data.razorpay_key_id,
        amount: orderRes.data.amount,
        currency: 'INR',
        name: 'Visual Institute of Arts',
        description: paymentMode === 'emi' ? `EMI Payment 1 - ${applicationId}` : `Admission - ${applicationId}`,
        order_id: orderRes.data.order_id,
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(`${API}/admissions/payment/verify`, {
              application_id: applicationId,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              payment_mode: paymentMode,
              emi_amount: payAmount
            }, { headers, withCredentials: true });
            setPaymentDone(true);
            setPaymentDetails({
              payment_id: response.razorpay_payment_id,
              order_id: response.razorpay_order_id,
              amount: payAmount,
              receipt_id: verifyRes.data.receipt_id,
              payment_mode: paymentMode,
              remaining: verifyRes.data.remaining_balance,
              total_fee: pricing.total
            });
            toast.success(verifyRes.data.message || 'Payment successful!');
          } catch { toast.error('Payment verification failed. Please contact support.'); }
        },
        prefill: { name: form.full_name, email: form.email, contact: form.mobile },
        theme: { color: '#10b981' }
      };

      const rzp = new Razorpay(options);
      rzp.on('payment.failed', () => toast.error('Payment failed. You can retry.'));
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not initiate payment');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalSubmit = async () => {
    await checkAuth();
    toast.success(`Admission complete! Application: ${applicationId}`);
    navigate('/student/dashboard');
  };

  const cardClass = theme === 'dark' ? 'bg-card/80 backdrop-blur border-border/50' : 'bg-white border shadow-lg';
  const stepBg = theme === 'dark' ? 'bg-muted/30' : 'bg-muted/50';

  return (
    <div className="min-h-screen pt-20 pb-12 relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern" />
      <div className="absolute inset-0 noise-overlay" />
      <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />

      <div className="relative max-w-3xl mx-auto px-4">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <Badge className="mb-3 px-3 py-1" variant="outline">Admissions Open</Badge>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">Student Admission Form</h1>
          <p className="text-muted-foreground">Visual Institute of Arts</p>
        </motion.div>

        {/* Program Type Tabs */}
        <div className="flex gap-3 justify-center mb-8">
          <Button variant={programType === 'vocational' ? 'default' : 'outline'} className="gap-2 px-6"
            onClick={() => { setProgramType('vocational'); setSelectedCourses({}); }} data-testid="tab-vocational">
            <Briefcase className="w-4 h-4" /> Vocational Courses
          </Button>
          <Button variant={programType === 'bdes' ? 'default' : 'outline'} className="gap-2 px-6"
            onClick={() => { setProgramType('bdes'); setSelectedCourses({}); }} data-testid="tab-bdes">
            <GraduationCap className="w-4 h-4" /> B.Des (Animation)
          </Button>
        </div>

        {/* Step Indicator */}
        <div className={`flex items-center justify-between mb-8 ${stepBg} rounded-2xl p-3 overflow-x-auto`}>
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            const done = i < currentStep;
            const active = i === currentStep;
            return (
              <React.Fragment key={step.id}>
                <button onClick={() => i < currentStep && i < 7 && setCurrentStep(i)}
                  className={`flex flex-col items-center gap-1 transition-all flex-shrink-0 ${active ? 'scale-105' : ''} ${i <= currentStep ? 'cursor-pointer' : 'cursor-default opacity-50'}`}
                  data-testid={`step-${step.id}`}>
                  <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all
                    ${done ? 'bg-primary text-primary-foreground' : active ? 'bg-primary text-primary-foreground ring-4 ring-primary/30' : 'bg-muted text-muted-foreground'}`}>
                    {done ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                  </div>
                  <span className={`text-[9px] sm:text-xs font-medium ${active ? 'text-primary' : 'text-muted-foreground'}`}>{step.label}</span>
                </button>
                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-0.5 min-w-[6px] ${i < currentStep ? 'bg-primary' : 'bg-muted'}`} />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Form Card */}
        <Card className={cardClass}>
          <CardContent className="p-5 sm:p-8">
            <AnimatePresence mode="wait">
              <motion.div key={currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>

                {/* STEP 0: Course Selection with Self-Learning / Instructor-Led */}
                {currentStep === 0 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="text-xl font-bold mb-1">Select Your Courses</h2>
                      <p className="text-sm text-muted-foreground">Choose courses and pick Self-Learning or Instructor-Led for each</p>
                    </div>

                    <div className="space-y-3">
                      {filteredCourses.map(course => {
                        const sel = selectedCourses[course.course_id];
                        const isSelected = !!sel;
                        const pt = course.pricing_tiers || {};
                        const hasTiers = pt.self_learning || pt.instructor_led;
                        const slPrice = pt.self_learning?.price ?? course.price;
                        const ilPrice = pt.instructor_led?.price ?? course.price;
                        const isFree = pricing.freeCourses.includes(course.course_id) && !isSelected;

                        return (
                          <div key={course.course_id}
                            className={`rounded-xl border transition-all ${isSelected ? 'border-primary ring-1 ring-primary/30 bg-primary/5' : isFree ? 'border-green-500/50 bg-green-500/5' : 'border-border'}`}
                            data-testid={`course-option-${course.course_id}`}>
                            <div className="p-4">
                              <div className="flex items-center gap-3 mb-3">
                                <Badge variant="outline" className="font-mono text-xs shrink-0">{course.course_id}</Badge>
                                <span className="font-semibold">{course.title}</span>
                                {isFree && <Badge className="bg-green-600 text-white ml-auto">FREE with offer</Badge>}
                              </div>
                              <div className="flex flex-wrap gap-1 mb-3">
                                {(course.skills || []).slice(0, 4).map((s, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs">{s}</Badge>
                                ))}
                              </div>

                              {/* Tier Selection */}
                              {hasTiers ? (
                                <div className="grid grid-cols-2 gap-3">
                                  <button
                                    onClick={() => toggleCourse(course.course_id, 'self_learning')}
                                    className={`p-3 rounded-lg border-2 text-left transition-all
                                      ${sel?.tier === 'self_learning' ? 'border-blue-500 bg-blue-500/10' : 'border-border hover:border-blue-500/40'}`}
                                    data-testid={`${course.course_id}-self-learning`}>
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
                                        ${sel?.tier === 'self_learning' ? 'border-blue-500 bg-blue-500' : 'border-muted-foreground'}`}>
                                        {sel?.tier === 'self_learning' && <Check className="w-3 h-3 text-white" />}
                                      </div>
                                      <span className="text-sm font-semibold">Self-Learning</span>
                                    </div>
                                    <p className="text-lg font-bold text-blue-500">{fmtPrice(slPrice)}</p>
                                    <p className="text-[11px] text-muted-foreground">Learn at your own pace</p>
                                  </button>

                                  <button
                                    onClick={() => toggleCourse(course.course_id, 'instructor_led')}
                                    className={`p-3 rounded-lg border-2 text-left transition-all
                                      ${sel?.tier === 'instructor_led' ? 'border-emerald-500 bg-emerald-500/10' : 'border-border hover:border-emerald-500/40'}`}
                                    data-testid={`${course.course_id}-instructor-led`}>
                                    <div className="flex items-center gap-2 mb-1">
                                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
                                        ${sel?.tier === 'instructor_led' ? 'border-emerald-500 bg-emerald-500' : 'border-muted-foreground'}`}>
                                        {sel?.tier === 'instructor_led' && <Check className="w-3 h-3 text-white" />}
                                      </div>
                                      <span className="text-sm font-semibold">Instructor-Led</span>
                                    </div>
                                    <p className="text-lg font-bold text-emerald-500">{fmtPrice(ilPrice)}</p>
                                    <p className="text-[11px] text-muted-foreground">Live classes with mentor</p>
                                  </button>
                                </div>
                              ) : (
                                <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:border-primary/50"
                                  onClick={() => toggleCourse(course.course_id, 'self_learning')}>
                                  <Checkbox checked={isSelected} />
                                  <span className="font-bold">{fmtPrice(course.price)}</span>
                                </label>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Offer Banner */}
                    {pricing.appliedOffer && (
                      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                        className="p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30">
                        <div className="flex items-center gap-2 mb-2">
                          <Gift className="w-5 h-5 text-green-500" />
                          <span className="font-bold text-green-500">Offer Applied!</span>
                        </div>
                        <p className="text-sm">{pricing.appliedOffer.description}</p>
                      </motion.div>
                    )}

                    {/* Price Summary */}
                    {selectedIds.length > 0 && (
                      <div className="p-4 rounded-xl border bg-muted/30">
                        {pricing.items.map(item => (
                          <div key={item.course_id} className="flex justify-between text-sm py-1">
                            <span>{item.course_id} <Badge variant="secondary" className="text-[10px] ml-1">{item.tier === 'instructor_led' ? 'Instructor-Led' : 'Self-Learning'}</Badge></span>
                            <span>{fmtPrice(item.price)}</span>
                          </div>
                        ))}
                        {pricing.discount > 0 && (
                          <div className="flex justify-between text-sm py-1 text-green-500"><span><Tag className="w-3 h-3 inline mr-1" />Discount</span><span>-{fmtPrice(pricing.discount)}</span></div>
                        )}
                        <Separator className="my-2" />
                        <div className="flex justify-between font-bold text-lg"><span>Total</span><span>{fmtPrice(pricing.total)}</span></div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Skill Level *</Label>
                        <Select value={form.skill_level} onValueChange={v => update('skill_level', v)}>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="beginner">Beginner</SelectItem>
                            <SelectItem value="intermediate">Intermediate</SelectItem>
                            <SelectItem value="advanced">Advanced</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Batch Preferred *</Label>
                        <Select value={form.batch_preferred} onValueChange={v => update('batch_preferred', v)}>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="morning">Morning</SelectItem>
                            <SelectItem value="afternoon">Afternoon</SelectItem>
                            <SelectItem value="evening">Evening</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Mode *</Label>
                        <Select value={form.mode} onValueChange={v => update('mode', v)}>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="offline">Offline</SelectItem>
                            <SelectItem value="online">Online</SelectItem>
                            <SelectItem value="hybrid">Hybrid</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 1: Personal Details */}
                {currentStep === 1 && (
                  <div className="space-y-6">
                    <div><h2 className="text-xl font-bold mb-1">Personal Details</h2><p className="text-sm text-muted-foreground">Tell us about yourself</p></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2 sm:col-span-2">
                        <Label>Full Name *</Label>
                        <div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input value={form.full_name} onChange={e => { update('full_name', e.target.value); clearError('full_name'); }} className={`pl-10 ${errors.full_name ? 'border-red-500' : ''}`} placeholder="As per official documents" data-testid="input-fullname" /></div>
                        <FieldError field="full_name" />
                      </div>
                      <div className="space-y-2"><Label>Gender *</Label>
                        <Select value={form.gender} onValueChange={v => { update('gender', v); clearError('gender'); }}><SelectTrigger className={errors.gender ? 'border-red-500' : ''}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select>
                        <FieldError field="gender" /></div>
                      <div className="space-y-2"><Label>Date of Birth *</Label>
                        <div className="relative"><Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input type="date" value={form.date_of_birth} onChange={e => { update('date_of_birth', e.target.value); clearError('date_of_birth'); }} className={`pl-10 ${errors.date_of_birth ? 'border-red-500' : ''}`} data-testid="input-dob" /></div>
                        <FieldError field="date_of_birth" /></div>
                      <div className="space-y-2"><Label>Mobile Number *</Label>
                        <div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input value={form.mobile} onChange={e => handlePhoneInput('mobile', e.target.value)} className={`pl-10 ${errors.mobile ? 'border-red-500' : ''}`} placeholder="+91 98765 43210" data-testid="input-mobile" inputMode="tel" /></div>
                        <FieldError field="mobile" /></div>
                      <div className="space-y-2"><Label>Email ID *</Label>
                        <div className="relative"><Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input type="email" value={form.email} onChange={e => { update('email', e.target.value); clearError('email'); }} className={`pl-10 ${errors.email ? 'border-red-500' : ''}`} placeholder="you@email.com" data-testid="input-email" /></div>
                        <FieldError field="email" /></div>
                      <div className="space-y-2"><Label>Aadhaar Number</Label>
                        <Input value={form.aadhaar} onChange={e => handleAadhaarInput(e.target.value)} className={errors.aadhaar ? 'border-red-500' : ''} placeholder="XXXX XXXX XXXX" inputMode="numeric" maxLength={14} />
                        <FieldError field="aadhaar" /></div>
                    </div>
                    <Separator />
                    <div className="space-y-4">
                      <h3 className="font-semibold flex items-center gap-2"><MapPin className="w-4 h-4" /> Address</h3>
                      <div className="space-y-2"><Label>Current Address</Label><Textarea value={form.current_address} onChange={e => update('current_address', e.target.value)} rows={2} placeholder="Street, City, State, PIN" /></div>
                      <div className="space-y-2"><Label>Permanent Address</Label><Textarea value={form.permanent_address} onChange={e => update('permanent_address', e.target.value)} rows={2} placeholder="Leave blank if same as above" /></div>
                    </div>
                  </div>
                )}

                {/* STEP 2: Education */}
                {currentStep === 2 && (
                  <div className="space-y-6">
                    <div><h2 className="text-xl font-bold mb-1">Education Details</h2><p className="text-sm text-muted-foreground">Your academic background</p></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2 sm:col-span-2"><Label>Highest Qualification *</Label>
                        <Select value={form.highest_qualification} onValueChange={v => { update('highest_qualification', v); clearError('highest_qualification'); }}><SelectTrigger className={errors.highest_qualification ? 'border-red-500' : ''}><SelectValue placeholder="Select qualification" /></SelectTrigger>
                        <SelectContent><SelectItem value="10th">10th Standard</SelectItem><SelectItem value="12th">12th Standard</SelectItem><SelectItem value="graduate">Graduate</SelectItem><SelectItem value="postgraduate">Post Graduate</SelectItem><SelectItem value="diploma">Diploma</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select>
                        <FieldError field="highest_qualification" /></div>
                      <div className="space-y-2"><Label>School / College Name</Label><Input value={form.school_college} onChange={e => update('school_college', e.target.value)} placeholder="Institution name" /></div>
                      <div className="space-y-2"><Label>Percentage / Grade</Label>
                        <Input value={form.percentage_grade} onChange={e => { update('percentage_grade', e.target.value); clearError('percentage_grade'); }} className={errors.percentage_grade ? 'border-red-500' : ''} placeholder="e.g., 85% or A+" />
                        <FieldError field="percentage_grade" /></div>
                      <div className="space-y-2"><Label>Year of Passing</Label>
                        <Input value={form.year_of_passing} onChange={e => handleYearInput(e.target.value)} className={errors.year_of_passing ? 'border-red-500' : ''} placeholder="e.g., 2024" inputMode="numeric" maxLength={4} />
                        <FieldError field="year_of_passing" /></div>
                    </div>
                  </div>
                )}

                {/* STEP 3: Guardian */}
                {currentStep === 3 && (
                  <div className="space-y-6">
                    <div><h2 className="text-xl font-bold mb-1">Guardian Details</h2><p className="text-sm text-muted-foreground">Parent or guardian information</p></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2 sm:col-span-2"><Label>Parent / Guardian Name *</Label>
                        <div className="relative"><User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input value={form.guardian_name} onChange={e => { update('guardian_name', e.target.value); clearError('guardian_name'); }} className={`pl-10 ${errors.guardian_name ? 'border-red-500' : ''}`} placeholder="Full name" data-testid="input-guardian-name" /></div>
                        <FieldError field="guardian_name" /></div>
                      <div className="space-y-2"><Label>Contact Number *</Label>
                        <div className="relative"><Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input value={form.guardian_contact} onChange={e => handlePhoneInput('guardian_contact', e.target.value)} className={`pl-10 ${errors.guardian_contact ? 'border-red-500' : ''}`} placeholder="+91 98765 43210" data-testid="input-guardian-contact" inputMode="tel" /></div>
                        <FieldError field="guardian_contact" /></div>
                      <div className="space-y-2"><Label>Occupation</Label><Input value={form.guardian_occupation} onChange={e => update('guardian_occupation', e.target.value)} placeholder="e.g., Business, Service" /></div>
                    </div>
                  </div>
                )}

                {/* STEP 4: Documents */}
                {currentStep === 4 && (
                  <div className="space-y-6">
                    <div><h2 className="text-xl font-bold mb-1">Documents</h2><p className="text-sm text-muted-foreground">Upload required documents (optional now, can submit later)</p></div>
                    {[
                      { key: 'id_proof', label: 'ID Proof', desc: 'Aadhaar, PAN, Passport, or Voter ID' },
                      { key: 'photo', label: 'Passport Size Photo', desc: 'Recent photograph (JPG/PNG)' },
                      { key: 'education_proof', label: 'Education Certificate', desc: 'Latest marksheet or degree' },
                    ].map(doc => (
                      <div key={doc.key} className="p-4 rounded-xl border">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold">{doc.label}</p>
                            <p className="text-xs text-muted-foreground">{doc.desc}</p>
                          </div>
                          {form.documents[doc.key] ? (
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs"><Check className="w-3 h-3 mr-1" />{form.documents[doc.key].original_name}</Badge>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setForm(f => ({ ...f, documents: { ...f.documents, [doc.key]: null } }))}>
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          ) : (
                            <label className="cursor-pointer">
                              <input type="file" className="hidden" accept="image/*,.pdf" onChange={e => handleDocUpload(doc.key, e.target.files[0])} disabled={uploading} />
                              <Button variant="outline" size="sm" asChild disabled={uploading}>
                                <span>{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" /> Upload</>}</span>
                              </Button>
                            </label>
                          )}
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground">Max file size: 5MB. Accepted: JPG, PNG, PDF</p>
                  </div>
                )}

                {/* STEP 5: Account */}
                {currentStep === 5 && (
                  <div className="space-y-6">
                    <div><h2 className="text-xl font-bold mb-1">Create Your Account</h2><p className="text-sm text-muted-foreground">Set a password to access your student portal</p></div>
                    <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 text-sm"><p>Login email: <strong>{form.email || '(set in Personal Details)'}</strong></p></div>
                    <div className="space-y-4 max-w-md">
                      <div className="space-y-2"><Label>Password *</Label>
                        <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input type={showPassword ? 'text' : 'password'} value={form.password} onChange={e => { update('password', e.target.value); clearError('password'); }} className={`pl-10 pr-10 ${errors.password ? 'border-red-500' : ''}`} placeholder="Min 6 characters" data-testid="input-password" />
                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button></div>
                        <FieldError field="password" /></div>
                      <div className="space-y-2"><Label>Confirm Password *</Label>
                        <div className="relative"><Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input type={showPassword ? 'text' : 'password'} value={form.confirm_password} onChange={e => { update('confirm_password', e.target.value); clearError('confirm_password'); }} className={`pl-10 ${errors.confirm_password ? 'border-red-500' : ''}`} placeholder="Re-enter password" data-testid="input-confirm-password" /></div>
                        <FieldError field="confirm_password" /></div>
                    </div>
                  </div>
                )}

                {/* STEP 6: Review */}
                {currentStep === 6 && (
                  <div className="space-y-5">
                    <div><h2 className="text-xl font-bold mb-1">Review & Declaration</h2><p className="text-sm text-muted-foreground">Verify details before payment</p></div>
                    <div className="space-y-4">
                      <div className="p-4 rounded-lg border">
                        <h3 className="font-semibold text-xs text-muted-foreground mb-2">SELECTED COURSES</h3>
                        {pricing.items.map(item => (
                          <div key={item.course_id} className="flex justify-between text-sm py-1">
                            <span><Badge variant="outline" className="mr-2 font-mono text-xs">{item.course_id}</Badge>{item.title} <Badge variant="secondary" className="text-[10px] ml-1">{item.tier === 'instructor_led' ? 'Instructor-Led' : 'Self-Learning'}</Badge></span>
                            <span className="font-medium">{fmtPrice(item.price)}</span>
                          </div>
                        ))}
                        {pricing.freeCourseObjs.map(c => (
                          <div key={c.course_id} className="flex justify-between text-sm py-1 text-green-500"><span><Badge className="mr-2 bg-green-600 text-white text-xs">FREE</Badge>{c.title}</span><span>FREE</span></div>
                        ))}
                        {pricing.discount > 0 && <div className="flex justify-between text-sm py-1 text-green-500 border-t mt-2 pt-2"><span>Discount</span><span>-{fmtPrice(pricing.discount)}</span></div>}
                        <div className="flex justify-between font-bold border-t mt-2 pt-2"><span>Total Payable</span><span>{fmtPrice(pricing.total)}</span></div>
                      </div>
                      <div className="p-4 rounded-lg border">
                        <h3 className="font-semibold text-xs text-muted-foreground mb-2">PERSONAL</h3>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div><span className="text-muted-foreground">Name:</span> {form.full_name}</div>
                          <div><span className="text-muted-foreground">Gender:</span> {form.gender}</div>
                          <div><span className="text-muted-foreground">DOB:</span> {form.date_of_birth}</div>
                          <div><span className="text-muted-foreground">Mobile:</span> {form.mobile}</div>
                          <div><span className="text-muted-foreground">Email:</span> {form.email}</div>
                          <div><span className="text-muted-foreground">Batch:</span> {form.batch_preferred} | {form.mode}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="p-4 rounded-lg border"><h3 className="font-semibold text-xs text-muted-foreground mb-2">EDUCATION</h3><p className="text-sm">{form.highest_qualification} {form.school_college ? `- ${form.school_college}` : ''}</p></div>
                        <div className="p-4 rounded-lg border"><h3 className="font-semibold text-xs text-muted-foreground mb-2">GUARDIAN</h3><p className="text-sm">{form.guardian_name}</p><p className="text-sm text-muted-foreground">{form.guardian_contact}</p></div>
                      </div>
                      {Object.values(form.documents).some(d => d) && (
                        <div className="p-4 rounded-lg border"><h3 className="font-semibold text-xs text-muted-foreground mb-2">DOCUMENTS</h3>
                          <div className="flex flex-wrap gap-2">{Object.entries(form.documents).filter(([,v]) => v).map(([k,v]) => <Badge key={k} variant="outline"><FileText className="w-3 h-3 mr-1" />{v.original_name}</Badge>)}</div></div>
                      )}
                    </div>
                    <Separator />
                    <label className="flex items-start gap-3 p-4 rounded-lg border cursor-pointer hover:border-primary/50">
                      <Checkbox checked={agreed} onCheckedChange={setAgreed} className="mt-0.5" data-testid="declaration-checkbox" />
                      <p className="text-sm leading-relaxed">I confirm that all information provided is true and correct. I understand that registration is subject to verification and admission approval by Visual Institute of Arts.</p>
                    </label>
                  </div>
                )}

                {/* STEP 7: Payment */}
                {currentStep === 7 && (
                  <div className="space-y-6">
                    <div><h2 className="text-xl font-bold mb-1">Payment</h2><p className="text-sm text-muted-foreground">{paymentDone ? 'Payment completed! Submit your application.' : 'Choose payment option and complete payment'}</p></div>
                    {!paymentDone ? (
                      <div className="space-y-4">
                        {/* Total Fee Display */}
                        <div className="p-4 rounded-xl border text-center">
                          <p className="text-sm text-muted-foreground mb-1">Total Course Fee</p>
                          <p className="text-3xl font-bold">{fmtPrice(pricing.total)}</p>
                          <p className="text-xs text-muted-foreground">{selectedIds.length} course(s){pricing.discount > 0 ? ` (${fmtPrice(pricing.discount)} discount applied)` : ''}</p>
                          {applicationId && <Badge variant="outline" className="font-mono mt-2">{applicationId}</Badge>}
                        </div>

                        {/* Payment Mode Toggle */}
                        <div className="grid grid-cols-2 gap-3" data-testid="payment-mode-toggle">
                          <button
                            onClick={() => setPaymentMode('lumpsum')}
                            className={`p-4 rounded-xl border-2 text-left transition-all ${paymentMode === 'lumpsum' ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/30'}`}
                            data-testid="payment-mode-lumpsum"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold">Lumpsum</span>
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMode === 'lumpsum' ? 'border-primary' : 'border-muted-foreground/40'}`}>
                                {paymentMode === 'lumpsum' && <div className="w-3 h-3 rounded-full bg-primary" />}
                              </div>
                            </div>
                            <p className="text-2xl font-bold">{fmtPrice(pricing.total)}</p>
                            <p className="text-xs text-muted-foreground mt-1">Pay full amount at once</p>
                          </button>

                          <button
                            onClick={() => setPaymentMode('emi')}
                            className={`p-4 rounded-xl border-2 text-left transition-all ${paymentMode === 'emi' ? 'border-emerald-500 bg-emerald-500/5' : 'border-border hover:border-muted-foreground/30'}`}
                            data-testid="payment-mode-emi"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold">EMI</span>
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMode === 'emi' ? 'border-emerald-500' : 'border-muted-foreground/40'}`}>
                                {paymentMode === 'emi' && <div className="w-3 h-3 rounded-full bg-emerald-500" />}
                              </div>
                            </div>
                            <p className="text-2xl font-bold text-emerald-500">{fmtPrice(EMI_MIN)}</p>
                            <p className="text-xs text-muted-foreground mt-1">Start now, pay rest monthly</p>
                          </button>
                        </div>

                        {/* EMI Breakdown */}
                        {paymentMode === 'emi' && (
                          <div className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 space-y-2" data-testid="emi-breakdown">
                            <div className="flex justify-between text-sm">
                              <span>Pay Today (1st EMI)</span>
                              <span className="font-bold text-emerald-500">{fmtPrice(EMI_MIN)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span>Remaining Balance</span>
                              <span className="font-medium">{fmtPrice(pricing.total - EMI_MIN)}</span>
                            </div>
                            <hr className="border-border/50" />
                            <p className="text-xs text-muted-foreground">
                              Pay remaining in monthly installments (min. {fmtPrice(EMI_MIN)}/month). 
                              You'll get full course access immediately after first payment.
                              Monthly reminders will be sent with a payment link.
                            </p>
                          </div>
                        )}

                        {/* Pay Button */}
                        <Button onClick={handlePayment} disabled={loading || razorpayLoading} className="w-full h-12 text-lg" data-testid="pay-now-btn">
                          {loading ? (
                            <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</>
                          ) : (
                            <><CreditCard className="w-5 h-5 mr-2" />Pay {fmtPrice(paymentMode === 'emi' ? EMI_MIN : pricing.total)} {paymentMode === 'emi' ? '(1st EMI)' : ''}</>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="p-6 rounded-xl border border-green-500/30 bg-green-500/5 text-center">
                        <div className="w-16 h-16 rounded-full bg-green-500 mx-auto mb-4 flex items-center justify-center"><Check className="w-8 h-8 text-white" /></div>
                        <h3 className="text-xl font-bold text-green-500 mb-2">Payment Successful</h3>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>Amount Paid: <strong>{fmtPrice(paymentDetails?.amount)}</strong>{paymentDetails?.payment_mode === 'emi' ? ' (1st EMI)' : ''}</p>
                          <p>Payment ID: <strong>{paymentDetails?.payment_id}</strong></p>
                          <p>Application: <strong>{applicationId}</strong></p>
                          {paymentDetails?.remaining > 0 && (
                            <p className="text-amber-400 mt-2">Remaining Balance: <strong>{fmtPrice(paymentDetails.remaining)}</strong> — payable via monthly EMI</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              <Button variant="outline" disabled={currentStep === 0 || currentStep === 7} onClick={handleBack} data-testid="btn-prev">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              {currentStep < 6 ? (
                <Button onClick={handleNext} data-testid="btn-next">Next <ChevronRight className="w-4 h-4 ml-1" /></Button>
              ) : currentStep === 6 ? (
                <Button onClick={handleNext} disabled={loading} className="px-8" data-testid="btn-proceed-payment">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</> : <>Proceed to Payment <ChevronRight className="w-4 h-4 ml-1" /></>}
                </Button>
              ) : currentStep === 7 && paymentDone ? (
                <Button onClick={handleFinalSubmit} className="px-8 bg-green-600 hover:bg-green-700" data-testid="btn-final-submit"><Check className="w-4 h-4 mr-2" /> Submit Application</Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
